import type { SegmentStatus, UploadErrorCode, UploadPhase } from './model'

interface UploadMessages {
    choose: string
    drop: string
    oneFile: string
    busy: string
    details: string
    cancel: string
    retry: string
    dismiss: string
    destination: string
    storage: string
    part: string
    cleanupWarning: string
    unconfirmed: string
    phases: Record<UploadPhase, string>
    segments: Record<SegmentStatus, string>
    errors: Record<UploadErrorCode, string>
}

const en: UploadMessages = {
    choose: 'New file',
    drop: 'or drop one file here',
    oneFile: 'Choose one file. Folder uploads are not supported yet.',
    busy: 'A file is already uploading. Wait for it to finish or cancel it.',
    details: 'Transfer details',
    cancel: 'Cancel',
    retry: 'Upload again from the start',
    dismiss: 'Dismiss',
    destination: 'Destination',
    storage: 'Storage',
    part: 'Part',
    cleanupWarning: 'Some temporary upload data could not be removed.',
    unconfirmed:
        'The server may still be assembling the file. Check the destination before uploading it again.',
    phases: {
        checking: 'Checking traffic and destination…',
        uploading: 'Uploading…',
        finalizing: 'Finalizing file…',
        cleaning: 'Finishing…',
        cancelling: 'Cancelling…',
        succeeded: 'File uploaded',
        failed: 'Upload failed',
        cancelled: 'Upload cancelled',
    },
    segments: {
        waiting: 'Waiting',
        uploading: 'Uploading',
        retrying: 'Retrying',
        done: 'Done',
        stopped: 'Stopped',
        failed: 'Failed',
    },
    errors: {
        quota: 'Not enough traffic is available, or it ran out during upload. Check your balance before trying again.',
        quotaCheck:
            'Traffic data is not available yet. Refresh the balance and try again.',
        network:
            'Unable to continue the upload. Check your connection and try again.',
        rateLimited:
            'The server is receiving too many requests. Please try again later.',
        unauthorized:
            'Access was denied. Sign in again and check access to this storage.',
        conflict:
            'A file or folder with this name already exists. Rename the file before uploading.',
        invalidFile: 'This file has an unsupported name or size.',
        protocol:
            'The server returned an unexpected response. The upload could not be confirmed.',
        offsetMismatch:
            'The server confirmed an unexpected file offset. Upload stopped to avoid corrupting the file.',
        worker: 'The browser could not continue processing the file. Please try again.',
        encryption:
            'Unable to prepare the file for transfer. Please try again.',
        finalizationFailed:
            'The server could not assemble the file. You can upload it again.',
        finalizationUnknown: 'Unable to confirm that the file is ready.',
    },
}

const ru: UploadMessages = {
    choose: 'Новый файл',
    drop: 'или перетащите сюда один файл',
    oneFile: 'Выберите один файл. Загрузка папок пока не поддерживается.',
    busy: 'Один файл уже загружается. Дождитесь завершения или отмените загрузку.',
    details: 'Подробности передачи',
    cancel: 'Отменить',
    retry: 'Загрузить заново',
    dismiss: 'Закрыть',
    destination: 'Куда',
    storage: 'Хранилище',
    part: 'Часть',
    cleanupWarning: 'Не все временные данные загрузки удалось удалить.',
    unconfirmed:
        'Сервер мог продолжить сборку файла. Проверьте папку назначения перед новой загрузкой.',
    phases: {
        checking: 'Проверяем трафик и папку…',
        uploading: 'Загружаем…',
        finalizing: 'Собираем файл…',
        cleaning: 'Завершаем…',
        cancelling: 'Отменяем…',
        succeeded: 'Файл загружен',
        failed: 'Ошибка загрузки',
        cancelled: 'Загрузка отменена',
    },
    segments: {
        waiting: 'Ожидает',
        uploading: 'Загружаем',
        retrying: 'Повторная попытка',
        done: 'Готово',
        stopped: 'Остановлено',
        failed: 'Ошибка',
    },
    errors: {
        quota: 'Доступного трафика недостаточно или он закончился во время загрузки. Проверьте остаток перед новой попыткой.',
        quotaCheck:
            'Данные о трафике пока недоступны. Обновите баланс и повторите попытку.',
        network:
            'Не удалось продолжить загрузку. Проверьте подключение и попробуйте ещё раз.',
        rateLimited:
            'Сервер получает слишком много запросов. Попробуйте позже.',
        unauthorized:
            'Доступ запрещён. Войдите заново и проверьте доступ к хранилищу.',
        conflict:
            'Файл или папка с таким именем уже существует. Переименуйте файл перед загрузкой.',
        invalidFile: 'Имя или размер этого файла не поддерживается.',
        protocol:
            'Сервер вернул неожиданный ответ. Не удалось подтвердить загрузку.',
        offsetMismatch:
            'Сервер подтвердил неожиданную позицию в файле. Загрузка остановлена, чтобы избежать повреждения данных.',
        worker: 'Браузеру не удалось продолжить обработку файла. Попробуйте ещё раз.',
        encryption:
            'Не удалось подготовить файл к передаче. Попробуйте ещё раз.',
        finalizationFailed:
            'Серверу не удалось собрать файл. Можно загрузить его заново.',
        finalizationUnknown: 'Не удалось подтвердить готовность файла.',
    },
}

export const uploadMessages = { en, ru }
